import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../store";
import { OmniPatient, PartialOmniPatient, Patient, WaitlistPatient } from "../types";
import { api, convertToCallablePhoneNumber } from "../utils/utils";

export interface PatientState {
  patients: OmniPatient[];
  archivedPatients: OmniPatient[];
  patientMap: { [patientId: string]: OmniPatient };
  errorMsg: string | undefined;
  loadingFetchPatients: boolean;
  loadingDeletePatient: boolean;
  loadingUpdatePatient: boolean;
  loadingUpdatePriorities: boolean;
  loadingCreatePatient: boolean;
  loadingGetPatient: boolean;
  loadingGetProviders: boolean;
  loadingFetchArchivedPatients: boolean;
  loadingUnarchivePatient: boolean;
  includeArchivedPatients: boolean;
  serverTotal: number;
  currentCursor: string | undefined;
  nextCursor: string | undefined;
  serverPageSize: number;
  allPatientsLoaded: boolean;
  pageCache: Record<string, { patients: OmniPatient[]; total: number; nextCursor: string | undefined }>;
}

const initialState: PatientState = {
  patients: [],
  archivedPatients: [],
  patientMap: {},
  errorMsg: "",
  loadingFetchPatients: false,
  loadingDeletePatient: false,
  loadingUpdatePatient: false,
  loadingUpdatePriorities: false,
  loadingCreatePatient: false,
  loadingGetPatient: false,
  loadingGetProviders: false,
  loadingFetchArchivedPatients: false,
  loadingUnarchivePatient: false,
  includeArchivedPatients: false,
  serverTotal: 0,
  currentCursor: undefined,
  nextCursor: undefined,
  serverPageSize: 25,
  allPatientsLoaded: false,
  pageCache: {},
};

type GetWaitlistPatientsResult = {
  patients: OmniPatient[];
  total: number;
  currentCursor: string | undefined;
  nextCursor: string | undefined;
  pageSize: number;
  allLoaded: boolean;
};

export const getWaitlistPatients = createAsyncThunk<
  GetWaitlistPatientsResult,
  { token: string; quietly?: boolean; cursor?: string; pageSize?: number; sortOrder?: "asc" | "desc" },
  { rejectValue: Error }
>(
  "waitlist/getWaitlistPatients",
  async ({ token, cursor, pageSize, sortOrder }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const includeArchived = state.patients.includeArchivedPatients;
      let url = `/patients/waitlist?includeArchived=${includeArchived}`;
      if (pageSize !== undefined) {
        url += `&pageSize=${pageSize}`;
      }
      if (cursor !== undefined) {
        url += `&cursor=${encodeURIComponent(cursor)}`;
      }
      if (sortOrder !== undefined) {
        url += `&sortOrder=${sortOrder}`;
      }

      const response = await api.get(url, token);

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to fetch waitlist patients"));
      }

      return {
        patients: response.data.data ?? [],
        total: response.data.total ?? 0,
        currentCursor: cursor,
        nextCursor: response.data.cursor,
        pageSize: response.data.pageSize ?? pageSize ?? 25,
        allLoaded: pageSize === undefined,
      };
    } catch (error) {
      return rejectWithValue(
        new Error("Failed to fetch waitlist patients: " + ((error as any)?.response?.data?.error || (error as Error).message || "Unknown error"))
      );
    }
  }
);

export const updatePatient = createAsyncThunk<void, { token: string; patient: PartialOmniPatient }, { rejectValue: Error }>(
  "waitlist/updatePatient",
  async ({ token, patient }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.patch(`/patients/waitlist/${patient.patientId}`, token, patient);
      if (response.status !== 200) {
        throw new Error("Failed to update patient");
      }

      // Dispatch getWaitlistPatient to fetch the updated patient data
      await dispatch(getWaitlistPatient({ token, patientId: patient.patientId }));
    } catch (error: any) {
      return rejectWithValue(new Error("Failed to update patient: " + (error.response?.data?.error || error.message || "Unknown error")));
    }
  }
);

export const getWaitlistPatient = createAsyncThunk<OmniPatient, { token: string; patientId: string }, { rejectValue: Error }>(
  "waitlist/getWaitlistPatient",
  async ({ token, patientId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/patients/waitlist/${patientId}`, token);

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to fetch patient details"));
      }

      return response.data.patient;
    } catch (error) {
      return rejectWithValue(
        new Error("Failed to fetch patient details: " + (error as any)?.response?.data?.error || (error as Error).message || "Unknown error")
      );
    }
  }
);

export const archivePatient = createAsyncThunk<string, { token: string; patientId: string }, { rejectValue: Error }>(
  "waitlist/archivePatient",
  async ({ token, patientId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/patients/${patientId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to archive patient"));
      }

      const { allPatientsLoaded, currentCursor, serverPageSize } = (getState() as RootState).patients;
      if (allPatientsLoaded) {
        await dispatch(getWaitlistPatients({ token, quietly: true }));
      } else {
        await dispatch(getWaitlistPatients({ token, quietly: true, cursor: currentCursor, pageSize: serverPageSize }));
      }
      await dispatch(getArchivedPatients({ token }));

      return patientId;
    } catch (error) {
      return rejectWithValue(
        new Error("Failed to archive patient: " + ((error as any)?.response?.data?.error || (error as Error).message || "Unknown error"))
      );
    }
  }
);

export const unarchivePatient = createAsyncThunk<string, { token: string; patientId: string }, { rejectValue: Error }>(
  "waitlist/unarchivePatient",
  async ({ token, patientId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/patients/${patientId}/unarchive`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to unarchive patient"));
      }

      const { allPatientsLoaded, currentCursor, serverPageSize } = (getState() as RootState).patients;
      if (allPatientsLoaded) {
        await dispatch(getWaitlistPatients({ token, quietly: true }));
      } else {
        await dispatch(getWaitlistPatients({ token, quietly: true, cursor: currentCursor, pageSize: serverPageSize }));
      }
      await dispatch(getArchivedPatients({ token }));

      return patientId;
    } catch (error) {
      return rejectWithValue(
        new Error("Failed to unarchive patient: " + (error as any)?.response?.data?.error || (error as Error).message || "Unknown error")
      );
    }
  }
);

export const getArchivedPatients = createAsyncThunk<OmniPatient[], { token: string }, { rejectValue: Error }>(
  "waitlist/getArchivedPatients",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/patients/archived`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to fetch archived patients"));
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        new Error("Failed to fetch archived patients: " + (error as any)?.response?.data?.error || (error as Error).message || "Unknown error")
      );
    }
  }
);

export const createPatient = createAsyncThunk<OmniPatient, { token: string; omniPatient: OmniPatient }, { rejectValue: Error }>(
  "waitlist/createPatient",
  async ({ token, omniPatient }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/patients`,
        { omniPatient },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      dispatch(addPatient(response.data));

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to create patient"));
      }

      return response.data.patient;
    } catch (error: any) {
      return rejectWithValue(new Error("Failed to create patient: " + (error.response?.data?.error || error.message || "Unknown error")));
    }
  }
);

export const updatePriorities = createAsyncThunk<void, { token: string; patientIds: string[] }, { rejectValue: Error }>(
  "waitlist/updatePriorities",
  async ({ token, patientIds }, { rejectWithValue }) => {
    try {
      const priorities = patientIds.map((patientId, index) => ({
        patientId: patientId,
        priority: index + 1,
      }));

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/patients/priorities`, priorities, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status !== 200) {
        return rejectWithValue(new Error("Failed to update priorities"));
      }
    } catch (error) {
      return rejectWithValue(
        new Error("Failed to update priorities: " + (error as any)?.response?.data?.error || (error as Error).message || "Unknown error")
      );
    }
  }
);

const patientSlice = createSlice({
  name: "patientSlice",
  initialState,
  reducers: {
    addPatient: (state, action: PayloadAction<OmniPatient>) => {
      state.patients.push(action.payload);
    },
    updatePatientPriority: (state, action: PayloadAction<{ patientId: string; priority: number }>) => {
      state.patientMap[action.payload.patientId] = state.patientMap[action.payload.patientId]
        ? {
            ...state.patientMap[action.payload.patientId],
            waitlist: { ...state.patientMap[action.payload.patientId].waitlist, priority: action.payload.priority },
          }
        : state.patientMap[action.payload.patientId];
      state.patients = state.patients
        .map((patient) =>
          patient.patientId === action.payload.patientId
            ? { ...patient, waitlist: { ...patient.waitlist, priority: action.payload.priority } }
            : patient
        )
        .sort((a, b) => a.waitlist.priority - b.waitlist.priority);
    },
    setIncludeArchivedPatients: (state, action: PayloadAction<boolean>) => {
      state.includeArchivedPatients = action.payload;
    },
    restorePageFromCache: (state, action: PayloadAction<string | undefined>) => {
      const cacheKey = action.payload ?? "start";
      const cached = state.pageCache[cacheKey];
      if (cached) {
        state.patients = cached.patients;
        state.serverTotal = cached.total;
        state.currentCursor = action.payload;
        state.nextCursor = cached.nextCursor;
        const patientMap: { [patientId: string]: OmniPatient } = {};
        cached.patients.forEach((patient) => {
          patientMap[patient.patient.patientId] = patient;
        });
        state.patientMap = patientMap;
      }
    },
    clearPageCache: (state) => {
      state.pageCache = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPatient.pending, (state) => {
        state.loadingCreatePatient = true;
        state.errorMsg = "";
      })
      .addCase(createPatient.fulfilled, (state, action) => {
        state.loadingCreatePatient = false;
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.loadingCreatePatient = false;
        state.errorMsg = action.payload?.message;
      })
      .addCase(updatePatient.pending, (state) => {
        state.loadingUpdatePatient = true;
        state.errorMsg = "";
      })
      .addCase(updatePatient.fulfilled, (state) => {
        state.loadingUpdatePatient = false;
        // The patient object will be updated by getWaitlistPatient, so no direct changes here
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.loadingUpdatePatient = false;
        state.errorMsg = action.payload?.message;
      })
      .addCase(getWaitlistPatient.pending, (state) => {
        state.loadingGetPatient = true;
        state.errorMsg = "";
      })
      .addCase(getWaitlistPatient.fulfilled, (state, action) => {
        state.loadingGetPatient = false;

        state.patientMap[action.payload.patientId] = action.payload;

        const index = state.patients.findIndex((patient) => patient.patientId === action.payload.patientId);

        if (index !== -1) {
          state.patients[index] = action.payload;
        } else {
          state.patients.push(action.payload);
        }
      })
      .addCase(getWaitlistPatients.pending, (state, action) => {
        // Only set loadingFetchPatients to true if quietly is false or undefined
        if (!action.meta.arg.quietly) {
          state.loadingFetchPatients = true;
        }
        state.errorMsg = "";
      })
      .addCase(getWaitlistPatients.fulfilled, (state, action) => {
        state.loadingFetchPatients = false;
        state.patients = action.payload.patients;
        state.serverTotal = action.payload.total;
        state.currentCursor = action.payload.currentCursor;
        state.nextCursor = action.payload.nextCursor;
        state.serverPageSize = action.payload.pageSize ?? state.serverPageSize;
        state.allPatientsLoaded = action.payload.allLoaded;
        const patientMap: { [patientId: string]: OmniPatient } = {};
        action.payload.patients.forEach((patient) => {
          patientMap[patient.patient.patientId] = patient;
        });
        state.patientMap = patientMap;
        if (!action.payload.allLoaded) {
          const cacheKey = action.payload.currentCursor ?? "start";
          state.pageCache[cacheKey] = {
            patients: action.payload.patients,
            total: action.payload.total,
            nextCursor: action.payload.nextCursor,
          };
        }
      })
      .addCase(getWaitlistPatients.rejected, (state, action) => {
        state.loadingFetchPatients = false;
        state.errorMsg = action.payload?.message ?? action.error.message;
      })
      .addCase(archivePatient.pending, (state) => {
        state.loadingDeletePatient = true;
        state.errorMsg = "";
      })
      .addCase(archivePatient.fulfilled, (state, action) => {
        state.loadingDeletePatient = false;
        //state.patients = state.patients.filter((patient) => patient.patientId !== action.payload);
        //state.patientMap[action.payload].waitlist.archived = true;
      })
      .addCase(archivePatient.rejected, (state, action) => {
        state.loadingDeletePatient = false;
        state.errorMsg = action.payload?.message;
      })
      .addCase(updatePriorities.pending, (state) => {
        state.loadingUpdatePriorities = true;
        state.errorMsg = "";
      })
      .addCase(updatePriorities.fulfilled, (state) => {
        state.loadingUpdatePriorities = false;
      })
      .addCase(updatePriorities.rejected, (state, action) => {
        state.loadingUpdatePriorities = false;
        state.errorMsg = action.payload?.message;
      })
      .addCase(getArchivedPatients.pending, (state) => {
        state.loadingFetchArchivedPatients = true;
        state.errorMsg = "";
      })
      .addCase(getArchivedPatients.fulfilled, (state, action) => {
        state.loadingFetchArchivedPatients = false;
        state.archivedPatients = action.payload;
      })
      .addCase(getArchivedPatients.rejected, (state, action) => {
        state.loadingFetchArchivedPatients = false;
        state.errorMsg = action.payload?.message;
      })
      .addCase(unarchivePatient.pending, (state) => {
        state.loadingUnarchivePatient = true;
        state.errorMsg = "";
      })
      .addCase(unarchivePatient.fulfilled, (state, action) => {
        state.loadingUnarchivePatient = false;
      })
      .addCase(unarchivePatient.rejected, (state, action) => {
        state.loadingUnarchivePatient = false;
        state.errorMsg = action.payload?.message;
      });
  },
});

export const { addPatient, updatePatientPriority, setIncludeArchivedPatients, restorePageFromCache, clearPageCache } = patientSlice.actions;

export const selectWaitlistState = (state: RootState) => state.patients;

export default patientSlice.reducer;
